import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Rx';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { ONBOARD_CONSTANTS } from '../../app.constants';
import {
  ReserveRequestModel, CheckoutRequestModel, SaveCardRequestModel,
  ConformResponse, ConformRequest, PaymentInformation, CreditCard, PaymentOptionsInput,
  PaymentInfo
} from './payment.model';
import { AppService } from '../../app.service';
import { HttpService } from '../../services/http.service';
import { BuywifiService } from '../../services/buywifi.service';
import { ErrorService } from './../error/error.service';
import { TravelersService } from './../travelers/travelers.service';
import { APP_TRANSLATE } from '../../app.translation.en';
import { SharedService } from '@flysas/payment';
import { Router } from '@angular/router';


@Injectable()
export class PaymentService {

  private isSavedCardsEnable: boolean;
  private maxRetry: any;
  private maxRetryData: any;
  private ccFilled: boolean;
  private selectedGroup: string;
  private showLoaderPayBtn = new BehaviorSubject<boolean>(false);
  public getLoaderPayBtn$ = this.showLoaderPayBtn.asObservable();
  private paymentInfo = new BehaviorSubject<Array<PaymentInfo>>(null);
  public getPaymentInfo$ = this.paymentInfo.asObservable();
  private isGiftCardAdded = new BehaviorSubject<boolean>(false);
  public getIsGiftCardAdded$ = this.isGiftCardAdded.asObservable();
  private giftCardTotal = new BehaviorSubject<number>(null);
  public getGiftCardTotal$ = this.giftCardTotal.asObservable();
  private confirmTriggered = new BehaviorSubject<boolean>(false);
  public getConfirmTriggered$ = this.confirmTriggered.asObservable();
  private maxretryError = new BehaviorSubject<boolean>(false);
  public getMaxRetryError$ = this.maxretryError.asObservable();

  setMaxRetryError(maxretryError) {
    this.maxretryError.next(maxretryError);
  }

  setConfirmTriggered(isTriggerd) {
    this.confirmTriggered.next(isTriggerd);
  }

  setGiftCardTotal(total) {
    this.giftCardTotal.next(total);
  }

  setPaymentInfo(paymentInfo) {
    this.paymentInfo.next(paymentInfo);
  }

  setIsGiftCard(isGiftCard) {
    this.isGiftCardAdded.next(isGiftCard);
  }

  setLoaderPayBtn(loader: boolean) {
    this.showLoaderPayBtn.next(loader);
  }

  setSelectedGroup(grp: string) {
    this.selectedGroup = grp;
  }

  getSelectedGroup() {
    return this.selectedGroup;
  }


  constructor(private buywifiService: BuywifiService,
    private appService: AppService,
    private httpService: HttpService,
    private errorService: ErrorService,
    private travelersService: TravelersService,
    private translate: TranslateService,
    private sharedService: SharedService,
    private router: Router) { }

  getReserveRequest(sessionValue: any): ReserveRequestModel {
    const passengerResponse = this.buywifiService.getPassengerResponse();
    const offersResponse = this.buywifiService.getOffersResponse();
    const passengerId = passengerResponse.reservationInfo.reservationPassengerId;
    const pointOfSale = this.buywifiService.currencyPos;
    const contextName = this.appService.getContext();
    const channelName = this.appService.getChannel();
    const reserveRequest = {
      url: environment.apiBaseUrl + ONBOARD_CONSTANTS.RESERVE_PRODUCT_URL,
      data: {
        'pos': pointOfSale,
        'channel': channelName,
        'context': contextName,
        'reservationId': passengerResponse.reservationInfo.reservationId,
        'offerId': offersResponse.offerId,
        'bookingType': 'REV',
        'passengers': {
          'PASSENGERID': {
            'WIFI': [
              {
                'connectionId': this.buywifiService.connectionId,
                'segmentId': passengerResponse.reservationInfo.reservationFlightSegmentId,
                'quantity': sessionValue
              }
            ]
          }
        }
      },
      headers: { 'Accept-Language': `${this.appService.getLanguage()}_${this.appService.getPOS()}` }
    }
    const old = JSON.stringify(reserveRequest).replace('PASSENGERID', passengerId); // convert to JSON string    
    return JSON.parse(old);
  }

  updateCCFormError(value) {
    this.sharedService.setCCError(value);
  }

  getCheckoutRequest(checkoutId, dcpIdentifier): CheckoutRequestModel {
    const checkoutUrl = ONBOARD_CONSTANTS.CHECKOUT_URL.replace('<<CHECKOUTID>>', checkoutId);
    const saveCardRequest = {
      url: environment.apiBaseUrl + checkoutUrl,
      data: {
        'paymentInfo': {
          'creditCard': {
            'dcpId': dcpIdentifier
          }
        }
      },
      headers: { 'Accept-Language': `${this.appService.getLanguage()}_${this.appService.getPOS()}` }
    }
    return saveCardRequest;
  }

  buildConfirmRequest(checkoutId, dcpId): ConformRequest {
    const request = new ConformRequest();
    const payInfo = new PaymentInformation();
    const creditCard = new CreditCard();
    creditCard.dcpId = dcpId;
    payInfo.creditCard = creditCard;
    request.paymentInfo = payInfo;
    return request;
  }

  getConfirmRequest(checkoutId, dcpId, grp): CheckoutRequestModel {
    const checkoutUrl = ONBOARD_CONSTANTS.CONFIRM_URL.replace('<<CHECKOUTID>>', checkoutId);

    let confirmRequest;
    if (grp === 'CC') {
      confirmRequest = {
        url: environment.apiBaseUrl + checkoutUrl,
        data: this.buildConfirmRequest(checkoutId, dcpId),
        headers: { 'Accept-Language': `${this.appService.getLanguage()}_${this.appService.getPOS()}` }
      }
    } else if (grp === 'EBK') {
      confirmRequest = {
        url: environment.apiBaseUrl + checkoutUrl,
        data: {
        },
        headers: { 'Accept-Language': `${this.appService.getLanguage()}_${this.appService.getPOS()}` }
      }
    }
    return confirmRequest;
  }

  getSaveCardRequest(dcpIdentifier): SaveCardRequestModel {
    const profileId = this.appService.getProfileId();
    const saveCardRequest = {
      url: environment.apiBaseUrl + ONBOARD_CONSTANTS.SAVECARD_URL.replace('<<CUSTOMERID>>', profileId),
      data: {
        'creditCardDetails': [
          {
            'dcpId': dcpIdentifier,
            'isValidationRequired': 'false'
          }
        ]
      },
      headers: { 'Accept-Language': `${this.appService.getLanguage()}_${this.appService.getPOS()}` }
    }
    return saveCardRequest;
  }

  doReserveCall(wifiKey): Observable<any> {
    const loaderTextList = { loaderHeaderText: APP_TRANSLATE.loaderText, loaderSubHeaderText: '' };
    this.appService.showLoaderWithText(loaderTextList);
    const reserveCallRequest = this.getReserveRequest(wifiKey);
    return this.httpService.postData(reserveCallRequest).map(
      reserveCallResponse => {
        if (!reserveCallResponse.checkoutId) {
          const errorCode = reserveCallResponse.messages[0].code;
          if (reserveCallResponse.messages && errorCode && errorCode.substring(0, 1) === '7') {
            const singleError = [];
            singleError.push(reserveCallResponse.messages[0].code);
            this.errorService.addError(singleError);
          } else {
            this.errorService.addError(this.buywifiService.getDefaultError());
          }
          this.appService.hideLoaderWithText();
        }
        return reserveCallResponse;
      },
      error => {
        return error;
      }
    );
  }
  setSaveCardEnable(status) {
    this.isSavedCardsEnable = status;
  }

  doCheckoutPayCall(checkoutId, dcpId): Observable<any> {
    const checkoutCallRequest = this.getCheckoutRequest(checkoutId, dcpId);
    return this.httpService.postData(checkoutCallRequest).map(
      checkoutCallResponse => {
        if (checkoutCallResponse.status === 'FAILURE') {
          this.afterCheckoutSuccess(checkoutCallResponse);
        }
        return checkoutCallResponse;
      },
      error => {
        return error;
      });
  }

  doSavecardCall(dcpId) {
    const saveCardRequest = this.getSaveCardRequest(dcpId);
    this.httpService.postData(saveCardRequest).subscribe(saveCallResponse => {
      if (saveCallResponse && saveCallResponse.errorInfo && saveCallResponse.errorInfo[0].errorCode) {
        const saveCardError = [];
        saveCardError.push({
          'errorCode': saveCallResponse.errorInfo[0].errorCode,
          'type': 'notification'
        });
        this.errorService.addError(saveCardError);
      }
    },
      error => { });
    this.appService.hideLoaderWithText();
  }

  getPaymentTnCData(): Observable<any> {
    const tncUrls = this.travelersService.getTnCUrls();
    return this.httpService.getHtml(tncUrls.sas).map(tncData => {
      return tncData;
    });
  }

  viewDefaultError() {
    this.errorService.addError(this.buywifiService.getDefaultError());
    this.appService.hideLoaderWithText();
  }

  setMaxretryLimit() {
    this.maxRetry = sessionStorage.getItem('maxRetry');
    if (this.maxRetry) {
      this.maxRetry = Number(this.maxRetry) + 1;
    } else {
      this.maxRetry = 1;
    }
    sessionStorage.setItem('maxRetry', this.maxRetry);
  }

  getMaxretryLimit() {    
    return sessionStorage.getItem('maxRetry');
  }

  clearMaxretryLimit() {
    this.maxRetry = 0;
    sessionStorage.setItem('maxRetry', this.maxRetry);
  }

  setMaxretryCountValue(payOptionResponse) {
    payOptionResponse.paymentGroups.forEach(paymentGroup => {
      if (paymentGroup.code === 'CC') {
        this.maxRetryData = paymentGroup.maxRetries;
      }
    });
  }

  getMaxretryCountValue() {
    return this.maxRetryData;
  }

  doCheckoutConfirmCall(checkoutId, dcpId, grp): Observable<ConformResponse> {
    const checkoutCallRequest = this.getConfirmRequest(checkoutId, dcpId, grp);
    return this.httpService.postData(checkoutCallRequest).map(
      checkoutCallResponse => {
        return this.checkoutConfirmCallCondition(checkoutCallResponse);
      },
      error => {
        return error;
      });
  }

  getSingleErrorCode(singleError, errorCode) {
    const saveCardError = [];
    this.translate.get([singleError]).subscribe(result => {
      if (result[singleError] !== singleError) {
        saveCardError.push(errorCode);
      } else {
        saveCardError.push({ 'errorCode': '7011044' });
      }
    });
    return saveCardError;
  }

  checkoutConfirmCallCondition(checkoutCallResponse) {
    if (checkoutCallResponse.status === 'CNF') {
      this.afterCheckoutSuccess(checkoutCallResponse);
    }
    return checkoutCallResponse;
  }


  afterCheckoutSuccess(checkoutCallResponse) {
    const errorCode = checkoutCallResponse.notification[0].code;
    const saveCardError = [];
    if (errorCode && errorCode.substring(0, 1) === '7') {
      const singleError = ONBOARD_CONSTANTS.ERROR_TRALSALION_KEY + errorCode;
      saveCardError.push(this.getSingleErrorCode(singleError, errorCode));
    } else {
      saveCardError.push({ 'errorCode': '7011044' });
    }
    if (this.isSavedCardsEnable) {
      saveCardError.push({ 'errorCode': '6091303', 'type': 'infoerror' });
    }
    this.errorService.addError(saveCardError);
    this.appService.hideLoaderWithText();
  }
  public getCCFilled(): boolean {
    return this.ccFilled;
  }

  public setCCFilled(isCCFilled: boolean): void {
    this.ccFilled = isCCFilled;

  }

  public setPaymentInputData() {
    this.setPaymentOptionData();
    this.sharedService.getIsCcFormValid$.subscribe(value => {
      if (value === true) {
        this.setCCFilled(true);
        this.updateCCFormError(false);
      } else {
        this.setCCFilled(false);
      }
    });
    this.sharedService.getCallUpsellOrMmbPage.subscribe(callMmbOrCki => {
      if (callMmbOrCki) {
        location.reload(true);
      }
    });
  }

  public setPaymentOptionData() {
    const paymentInput = new PaymentOptionsInput();
    paymentInput.channel = 'WEB';
    paymentInput.style = 'sasredesign.css';
    paymentInput.context = this.appService.context.onb;
    paymentInput.checkoutId = this.buywifiService.getCheckOutId();
    paymentInput.customerId = this.appService.getSessionStorage('basicProfile', 'customerSessionId');
    paymentInput.locale = this.appService.getLanguage();
    paymentInput.apiBaseUrl = this.appService.getApiBaseUrl();
    paymentInput.pos = this.buywifiService.currencyPos ? this.buywifiService.currencyPos.toLowerCase() : '';
    this.sharedService.setPaymentOptionsInput(paymentInput);

  }

}
